import { ColorV2 } from "config/colors";
import { TimeReportResolution, TimeReportStatusV2 } from "model/TimeReport";
import type { TFunction } from "i18next";
import {
  getTimeReportAbsenceResolutionLabel,
  isTimeReportAbsenceResolution,
} from "web-apps/company/utils/absenceLabels";

interface ResolveTimeReportStatusBannerParams {
  status: TimeReportStatusV2 | null;
  resolution: TimeReportResolution | null;
  translate: TFunction;
}

interface TimeReportStatusBannerModel {
  iconName: string;
  label: string;
  textColor: ColorV2;
  backgroundColor: string;
}

const resolveResolutionLabel = (
  resolution: TimeReportResolution | null,
  translate: TFunction,
): string | null => {
  if (resolution === TimeReportResolution.WITHDRAWN) {
    return null;
  }

  if (resolution === TimeReportResolution.OVERDUE) {
    return null;
  }

  if (resolution === TimeReportResolution.TIME_REPORTED) {
    return translate("CompanyTimeReport.TimeReported");
  }

  if (resolution != null && isTimeReportAbsenceResolution(resolution)) {
    return getTimeReportAbsenceResolutionLabel(resolution, translate);
  }

  return null;
};

type BannerVariant =
  | "approved"
  | "withdrawn"
  | "toApprove"
  | "unreported"
  | "overdue";

const toRgba = (hexColor: string, alpha: number): string => {
  const sanitizedHex = hexColor.replace("#", "");
  const red = Number.parseInt(sanitizedHex.slice(0, 2), 16);
  const green = Number.parseInt(sanitizedHex.slice(2, 4), 16);
  const blue = Number.parseInt(sanitizedHex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

interface BannerVariantPresentation {
  iconName: string;
  textColor: ColorV2;
  backgroundColor: string;
  labelKey: string;
}

const BANNER_VARIANT_PRESENTATION: Record<
  BannerVariant,
  BannerVariantPresentation
> = {
  approved: {
    iconName: "v2/checkmark",
    textColor: ColorV2.PositiveGreen,
    backgroundColor: toRgba(ColorV2.PositiveGreen, 0.14),
    labelKey: "TimeReports.StatusBanner.Approved",
  },
  withdrawn: {
    iconName: "v2/cross",
    textColor: ColorV2.NegativeRed,
    backgroundColor: toRgba(ColorV2.NegativeRed, 0.14),
    labelKey: "TimeReports.StatusBanner.Withdrawn",
  },
  toApprove: {
    iconName: "v2/clock",
    textColor: ColorV2.ElectricBlue,
    backgroundColor: toRgba(ColorV2.ElectricBlue, 0.14),
    labelKey: "TimeReports.ToApprove",
  },
  unreported: {
    iconName: "v2/document",
    textColor: ColorV2.DarkAmbigiousYellow,
    backgroundColor: toRgba(ColorV2.DarkAmbigiousYellow, 0.16),
    labelKey: "TimeReports.StatusBanner.Unreported",
  },
  overdue: {
    iconName: "v2/warning-triangle",
    textColor: ColorV2.NegativeRed,
    backgroundColor: toRgba(ColorV2.NegativeRed, 0.14),
    labelKey: "CompanyTimeReport.Overdue",
  },
};

const resolveVariant = (
  status: TimeReportStatusV2 | null,
  resolution: TimeReportResolution | null,
): BannerVariant => {
  if (resolution === TimeReportResolution.WITHDRAWN) {
    return "withdrawn";
  }

  if (resolution === TimeReportResolution.OVERDUE) {
    return "overdue";
  }

  if (
    status === TimeReportStatusV2.APPROVED ||
    status === TimeReportStatusV2.CLOSED
  ) {
    return "approved";
  }

  if (status === TimeReportStatusV2.SUBMITTED) {
    return "toApprove";
  }

  return "unreported";
};

const resolvePresentation = (
  variant: BannerVariant,
  resolution: TimeReportResolution | null,
): BannerVariantPresentation => {
  const presentation = BANNER_VARIANT_PRESENTATION[variant];

  if (
    variant === "approved" &&
    resolution != null &&
    isTimeReportAbsenceResolution(resolution)
  ) {
    return {
      ...presentation,
      textColor: ColorV2.AbsentPurple,
      backgroundColor: toRgba(ColorV2.AbsentPurple, 0.14),
    };
  }

  return presentation;
};

/** Resolve status-banner copy and styling for the time-report details panel. */
export const resolveTimeReportStatusBanner = (
  params: ResolveTimeReportStatusBannerParams,
): TimeReportStatusBannerModel => {
  const variant = resolveVariant(params.status, params.resolution);
  const presentation = resolvePresentation(variant, params.resolution);
  const statusLabel = params.translate(presentation.labelKey);
  const resolutionLabel = resolveResolutionLabel(
    params.resolution,
    params.translate,
  );
  const label =
    resolutionLabel == null
      ? statusLabel
      : `${statusLabel} - ${resolutionLabel}`;

  return {
    iconName: presentation.iconName,
    label,
    textColor: presentation.textColor,
    backgroundColor: presentation.backgroundColor,
  };
};
